import React, { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../components/UploadImage";
import { SectionField } from "./HomeTabs";
import { removeSpecialChars } from "../../../lib/utils";
import TinyMce from "../../components/TinyMce";

interface BreadcrumbItem {
  _id?: string
  heading?: string;
  subtext?: string;
  label?: string;
  metaDescription: string
  slug?: string;
  data?: {
    subtext: string;
    image: {
      title: string;
      url: string;
    }[]
    introduction: {
      title: string;
      description: string[];
    };
    keyInsights: {
      title: string;
      points: string[];
    }
    ourPerspective: {
      title: string;
      description: string[];
    }
  };
  buttonLabel?: string;
  image?: string;
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: SectionField) => void;
  form: any;
  breadcrumb?: BreadcrumbItem[];
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const HomeBanner: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  onDelete,
  form,
  breadcrumb,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (mode === "add") {
      form.setFieldsValue({
        breadcrumb: [
          {
            heading: "",
            subtext: "",
            label: "",
            metaDescription: "",
            slug: "",
            buttonLabel: "Discover Insight",
            image: [],
            data: {
              subtext: "",
              image: [],
              introduction: { title: "", description: [""] },
              keyInsights: { title: "", points: [""] },
              ourPerspective: { title: "", description: [""] },
            },
          },
        ],
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = breadcrumb?.[editingIndex];
      form.setFieldsValue({
        breadcrumb: [
          {
            heading: item?.heading || "",
            subtext: item?.subtext || "",
            label: item?.label || "",
            slug: item?.slug || "",
            metaDescription: item?.metaDescription || "",
            buttonLabel: item?.buttonLabel || "Discover Insight",
            image: item?.image ? [item.image] : [],
            data: item?.data || {
              subtext: "",
              image: [],
              introduction: { title: "", description: [""] },
              keyInsights: { title: "", points: [""] },
              ourPerspective: { title: "", description: [""] },
            },
          },
        ],
      });
    }
  }, [mode, editingIndex, breadcrumb, form]);

  const watchBreadcrumb = Form.useWatch("breadcrumb", form);
  useEffect(() => {
    if (!watchBreadcrumb) return;

    watchBreadcrumb.forEach((item: any, index: number) => {
      if (!item?._id && item?.label) {
        form.setFieldValue(
          ["breadcrumb", index, "slug"],
          removeSpecialChars(item.label)
        );
      }
    });
  }, [watchBreadcrumb, form]);

  return (
    <div>
      {mode === "list" && (
        <div className="hidden sm:block rounded-[20px] overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="bg-primary text-white">
                <th className="px-4 py-3 text-left">Image</th>
                <th className="px-4 py-3 text-left">Heading</th>
                <th className="px-4 py-3 text-left">Subtext</th>
                <th className="px-4 py-3 text-left">Tab</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9F9F9]">
              {breadcrumb?.length ? (
                breadcrumb.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-3">
                      <Image
                        src={item.image}
                        width={80}
                        height={50}
                        className="object-cover"
                      />
                    </td>
                    <td className="px-4 py-3">{item.heading}</td>
                    <td className="px-4 py-3">{item.subtext}</td>
                    <td className="px-4 py-3">{item.label}</td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-4 text-lg">
                        <EditOutlined
                          className="cursor-pointer text-primary"
                          onClick={() => {
                            setMode("edit");
                            setEditingIndex(index);
                          }}
                        />

                        <Popconfirm
                          title="Delete this banner?"
                          onConfirm={() => onDelete(index, "breadcrumb")}
                        >
                          <DeleteOutlined className="cursor-pointer text-red-500" />
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No breadcrumb added
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan={5} className="px-4 py-3 text-right">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setMode("add");
                      setEditingIndex(null);
                    }}
                  >
                    Add Banner
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {mode === "list" && (
        <div className="sm:hidden">
          <div className="flex justify-end pb-3">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Banner
              </Button>
          </div>
          {breadcrumb?.map((item, index) => (
            <div key={index} className="bg-[#F9F9F9] border rounded-lg p-4 space-y-4">
              <Image
                src={item.image}
                width="100%"
                height={200}
                className="object-cover"
              />

              <p>
                <b className="text-primary">Heading:</b>
                <div>{item.heading}</div>
              </p>
              <p>
                <b className="text-primary">Subtext:</b>
                <div>{item.subtext}</div>
              </p>
              <p>
                <b className="text-primary">Tab:</b> 
                <div>{item.label}</div>
              </p>

              <div className="flex justify-end text-primary gap-4 text-xl mt-3">
                <EditOutlined
                  onClick={() => {
                    setMode("edit");
                    setEditingIndex(index);
                  }}
                />
                <Popconfirm
                  title="Delete this banner?"
                  onConfirm={() => onDelete(index, "breadcrumb")}
                >
                  <DeleteOutlined className="text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode !== "list" && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name="breadcrumb">
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Heading"
                        name={[field.name, "heading"]}
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="enter heading..."/>
                      </Form.Item>

                      <Form.Item
                        label="Meta Description"
                        name={[field.name, "metaDescription"]}
                        rules={[{ required: true, message: "Meta Description is required" }]}
                      >
                        <TextArea rows={2} placeholder="enter meta description..."/>
                      </Form.Item>

                      <Form.Item
                        label="Slug"
                        name={[field.name, "slug"]}
                        rules={[{ required: true, message: "Slug is required" }]}
                      >
                        <Input disabled={!!breadcrumb?.[editingIndex ?? 0]?._id} />
                      </Form.Item>

                      <Form.Item
                        label="Subtext"
                        name={[field.name, "subtext"]}
                        rules={[{ required: true }]}
                      >
                        <TextArea rows={2} placeholder="enter subtext..."/>
                      </Form.Item>

                      <Form.Item
                        label="Slider Label"
                        name={[field.name, "label"]}
                      >
                        <Input placeholder="enter slider label..."/>
                      </Form.Item>

                      <Form.Item
                        label="Button Label"
                        name={[field.name, "buttonLabel"]}
                      >
                        <Input placeholder="enter button label..."/>
                      </Form.Item>

                      <Form.Item
                        label="Image"
                        name={[field.name, "image"]}
                        rules={[{ required: true }]}
                      >
                        <UploadImage count={1} listType="picture-card" />
                      </Form.Item>

                      <Form.Item label="Page Data">
                        <div className="border border-gray-200 rounded-xl p-5 bg-white space-y-6">

                          <Form.Item
                            label="Page Subtext"
                            name={[field.name, "data", "subtext"]}
                            rules={[{ required: true }]}
                          >
                            <TextArea rows={2} placeholder="enter subtext..."/>
                          </Form.Item>

                          <div className="border border-gray-300 rounded-lg p-5">
                            <div className="font-medium mb-4">Image Section</div>
                            <Form.List name={[field.name, "data", "image"]} initialValue={[""]}>
                              {(imageFields, { add, remove }) => (
                                <>
                                  {imageFields.map((imgField) => (
                                    <div key={imgField.key} className="flex gap-3 mb-4 border-b pb-4 last:border-b-0">
                                      <Form.Item
                                        {...imgField}
                                        label="Title"
                                        name={[imgField.name, "title"]}
                                        rules={[{ required: true }]}
                                        className="flex-1"
                                      >
                                        <Input placeholder="Image title" />
                                      </Form.Item>
                                      <Form.Item
                                        {...imgField}
                                        label="Upload Image"
                                        name={[imgField.name, "url"]}
                                        rules={[{ required: true }]}
                                        className="flex-1"
                                      >
                                        <UploadImage count={1} listType="picture-card" />
                                      </Form.Item>
                                      <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => remove(imgField.name)}
                                        className="self-end mb-1 rounded-full"
                                      />
                                    </div>
                                  ))}
                                  <Button 
                                    type="dashed" 
                                    onClick={() => add({ title: "", url: "" })} 
                                    block 
                                    icon={<PlusOutlined />}
                                  >
                                    Add Data Image
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </div>

                          <div className="border border-gray-300 rounded-lg p-5">
                            <div className="font-medium mb-4">Introduction</div>
                            <Form.Item
                              label="Title"
                              name={[field.name, "data", "introduction", "title"]}
                            >
                              <Input placeholder="enter introduction title..." />
                            </Form.Item>

                            <Form.List name={[field.name, "data", "introduction", "description"]} initialValue={[""]}>
                              {(descFields, { add, remove }) => (
                                <div>
                                  <div className="flex justify-between mb-3">
                                    <span className="text-sm font-medium">Description</span>
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")}>
                                      Add Description
                                    </Button>
                                  </div>
                                  {descFields.map((dField) => (
                                    <div key={dField.key} className="flex gap-3 mb-3">
                                      <Form.Item
                                        {...dField}
                                        name={[dField.name]}
                                        rules={[{ required: true, message: "Description is required" }]}
                                        className="flex-1 mb-0"
                                      >
                                        <TextArea rows={2} placeholder="enter description..." />
                                      </Form.Item>
                                      <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => remove(dField.name)}
                                        className="mt-1 rounded-full"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Form.List>
                          </div>

                          <div className="border border-gray-300 rounded-lg p-5">
                            <div className="font-medium mb-4">Key Insights</div>
                            <Form.Item
                              label="Title"
                              name={[field.name, "data", "keyInsights", "title"]}
                            >
                              <Input placeholder="enter key insights title..." />
                            </Form.Item>

                            <Form.List name={[field.name, "data", "keyInsights", "points"]} initialValue={[""]}>
                              {(pointFields, { add, remove }) => (
                                <div>
                                  <div className="flex justify-between mb-3">
                                    <span className="text-sm font-medium">Insights</span>
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")}>
                                      Add Insights
                                    </Button>
                                  </div>
                                  {pointFields.map((pField) => (
                                    <div key={pField.key} className="flex gap-3 mb-3">
                                      <Form.Item
                                        {...pField}
                                        name={[pField.name]}
                                        rules={[{ required: true, message: "Point is required" }]}
                                        className="flex-1 mb-0"
                                      >
                                        <Input placeholder="enter insights..." />
                                      </Form.Item>
                                      <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => remove(pField.name)}
                                        className="mt-1 rounded-full"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Form.List>
                          </div>

                          <div className="border border-gray-300 rounded-lg p-5">
                            <div className="font-medium mb-4">Our Perspective</div>
                            <Form.Item
                              label="Title"
                              name={[field.name, "data", "ourPerspective", "title"]}
                            >
                              <Input placeholder="enter our perspective title..." />
                            </Form.Item> 

                            <Form.List name={[field.name, "data", "ourPerspective", "description"]} initialValue={[""]}>
                              {(descFields, { add, remove }) => (
                                <div>
                                  <div className="flex justify-between mb-3">
                                    <span className="text-sm font-medium">Description</span>
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")}>
                                      Add Description
                                    </Button>
                                  </div>
                                  {descFields.map((dField) => (
                                    <div key={dField.key} className="flex gap-3 mb-3">
                                      <Form.Item
                                        {...dField}
                                        name={[dField.name]}
                                        rules={[{ required: true, message: "Description is required" }]}
                                        className="flex-1 mb-0"
                                      >
                                        <Input placeholder="enter description..." />
                                      </Form.Item>
                                      <Button
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => remove(dField.name)}
                                        className="mt-1 rounded-full"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Form.List>
                          </div>

                        </div>
                      </Form.Item>

                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeBanner;